import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails,
  Switch, TextField, Chip, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Pagination,
  CircularProgress, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Tooltip, Grid, Card, CardContent, FormControlLabel, RadioGroup, Radio,
  FormLabel, Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, NotificationsActive as BellIcon,
  Email as EmailIcon, WhatsApp as WhatsAppIcon, Event as EventIcon,
  Add as AddIcon, Delete as DeleteIcon, PlayArrow as PlayIcon,
  Send as SendIcon, ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { notificationService } from '../services/api';

const CATEGORY_COLORS = {
  Content: '#1976d2',
  Employee: '#388e3c',
  Leave: '#f57c00',
  Expense: '#7b1fa2',
  Tasks: '#0288d1',
  Recruitment: '#c62828',
  Salary: '#00695c',
  Inventory: '#5d4037',
  Custom: '#546e7a',
};

function StatusChip({ status }) {
  const color = status === 'sent' ? 'success' : status === 'failed' ? 'error' : 'default';
  return <Chip label={status} color={color} size="small" />;
}

function ChannelChip({ channel }) {
  const icon = channel === 'email' ? '📧' : channel === 'whatsapp' ? '💬' : '🔔';
  return <Chip label={`${icon} ${channel}`} size="small" variant="outlined" />;
}

export default function Notifications() {
  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState({});
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logFilters, setLogFilters] = useState({ event_type: '', channel: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [testingEvent, setTestingEvent] = useState(null);
  const [addEventDialog, setAddEventDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({ type: '', label: '', category: 'Custom' });
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [waExpanded, setWaExpanded] = useState(false);

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationService.getSettings();
      const cfg = res.data.config || {};
      setConfig(cfg);
      try {
        setEvents(JSON.parse(cfg.events_list || '[]'));
      } catch (e) {
        setEvents([]);
      }
    } catch (err) {
      showSnack('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const params = { page: logPage, limit: 50, ...logFilters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await notificationService.getLogs(params);
      setLogs(res.data.logs || []);
      setLogTotal(res.data.total || 0);
    } catch (err) {
      showSnack('Failed to load logs', 'error');
    }
  }, [logPage, logFilters]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { if (tab === 1) loadLogs(); }, [tab, loadLogs]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await notificationService.updateSettings(config);
      showSnack('Settings saved successfully');
    } catch (err) {
      showSnack('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (channel) => {
    try {
      setTesting(true);
      await notificationService.sendTest(channel);
      showSnack(`Test ${channel} notification sent — check Delivery Logs`);
    } catch (err) {
      showSnack('Test notification failed', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleTriggerReport = async (type) => {
    try {
      await notificationService.triggerReport(type);
      showSnack(`${type} report notification triggered`);
    } catch (err) {
      showSnack('Failed to trigger report', 'error');
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleEventToggle = async (eventType, enabled) => {
    const updated = { ...config, [`event_${eventType}`]: String(enabled) };
    setConfig(updated);
    await notificationService.updateSettings({ [`event_${eventType}`]: String(enabled) });
  };

  const handleEventFieldChange = (eventType, field, value) => {
    setConfig(prev => ({ ...prev, [`event_${eventType}_${field}`]: value }));
  };

  const handleTestEvent = async (eventType) => {
    if (config.whatsapp_enabled !== 'true') {
      showSnack('Enable WhatsApp (and set recipients) before sending a test', 'warning');
      return;
    }
    try {
      setTestingEvent(eventType);
      await notificationService.testEvent(eventType, 'whatsapp');
      showSnack(`Test WhatsApp sent for "${eventType}" — check Delivery Logs`);
    } catch (err) {
      showSnack('Failed to send test', 'error');
    } finally {
      setTestingEvent(null);
    }
  };

  const handleSaveEventOverrides = async (eventType) => {
    try {
      const keys = [`event_${eventType}_whatsapp_recipients`, `event_${eventType}_whatsapp_groups`];
      const partial = {};
      keys.forEach(k => { partial[k] = config[k] || ''; });
      await notificationService.updateSettings(partial);
      showSnack('Event overrides saved');
    } catch (err) {
      showSnack('Failed to save event overrides', 'error');
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.type || !newEvent.label) {
      showSnack('Type and label are required', 'error');
      return;
    }
    try {
      const res = await notificationService.addEvent(newEvent);
      setEvents(res.data.events);
      setConfig(prev => ({ ...prev, events_list: JSON.stringify(res.data.events), [`event_${newEvent.type}`]: 'false' }));
      setAddEventDialog(false);
      setNewEvent({ type: '', label: '', category: 'Custom' });
      showSnack('Event added');
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to add event', 'error');
    }
  };

  const handleDeleteEvent = async (eventType) => {
    if (!window.confirm(`Delete event "${eventType}"? This cannot be undone.`)) return;
    try {
      const res = await notificationService.deleteEvent(eventType);
      setEvents(res.data.events);
      const updatedConfig = { ...config };
      delete updatedConfig[`event_${eventType}`];
      delete updatedConfig[`event_${eventType}_whatsapp_recipients`];
      delete updatedConfig[`event_${eventType}_whatsapp_groups`];
      updatedConfig.events_list = JSON.stringify(res.data.events);
      setConfig(updatedConfig);
      showSnack('Event deleted');
    } catch (err) {
      showSnack('Failed to delete event', 'error');
    }
  };

  const emailEnabled = config.email_enabled === 'true';
  const waEnabled = config.whatsapp_enabled === 'true';
  const waProvider = config.whatsapp_provider || 'ultramsg';

  const groupedEvents = events.reduce((acc, ev) => {
    const cat = ev.category || 'Custom';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ev);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <BellIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Notifications</Typography>
            <Typography variant="body2" color="text.secondary">Configure email and WhatsApp alerts for key events</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" startIcon={<PlayIcon />} onClick={() => handleTriggerReport('weekly')}>
            Send Weekly
          </Button>
          <Button variant="outlined" size="small" startIcon={<PlayIcon />} onClick={() => handleTriggerReport('monthly')}>
            Send Monthly
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Settings" />
        <Tab label="Delivery Logs" />
      </Tabs>

      {/* ── Settings Tab ── */}
      {tab === 0 && (
        <Box>
          {/* Email Notifications */}
          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Box
              display="flex" alignItems="center" justifyContent="space-between" px={2.5} py={2}
              sx={{ cursor: 'pointer' }} onClick={() => setEmailExpanded(p => !p)}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <EmailIcon color="action" />
                <Typography fontWeight={600}>Email Notifications</Typography>
                <Chip label={emailEnabled ? 'Enabled' : 'Disabled'} size="small"
                  color={emailEnabled ? 'success' : 'default'} />
              </Box>
              {emailExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={emailExpanded}>
              <Divider />
              <Box px={3} py={2.5}>
                <FormControlLabel
                  control={
                    <Switch checked={emailEnabled} onChange={e => handleConfigChange('email_enabled', String(e.target.checked))} />
                  }
                  label="Enable Email Notifications"
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="SMTP Host" value={config.email_smtp_host || ''}
                      onChange={e => handleConfigChange('email_smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="SMTP Port" value={config.email_smtp_port || ''}
                      onChange={e => handleConfigChange('email_smtp_port', e.target.value)} placeholder="587" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="SMTP Username / Email" value={config.email_smtp_user || ''}
                      onChange={e => handleConfigChange('email_smtp_user', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="SMTP Password / App Password" type="password"
                      value={config.email_smtp_pass || ''}
                      onChange={e => handleConfigChange('email_smtp_pass', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="From Name" value={config.email_from_name || ''}
                      onChange={e => handleConfigChange('email_from_name', e.target.value)} placeholder="JBDE OneHub" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Recipient Email(s)" value={config.email_to || ''}
                      onChange={e => handleConfigChange('email_to', e.target.value)}
                      placeholder="owner@example.com, team@example.com"
                      helperText="Comma-separated email addresses" />
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" gap={1}>
                  <Button size="small" variant="outlined" startIcon={<SendIcon />}
                    onClick={() => handleTest('email')} disabled={testing}>
                    Send Test Email
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {/* WhatsApp Notifications */}
          <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Box
              display="flex" alignItems="center" justifyContent="space-between" px={2.5} py={2}
              sx={{ cursor: 'pointer' }} onClick={() => setWaExpanded(p => !p)}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WhatsAppIcon color="action" />
                <Typography fontWeight={600}>WhatsApp Notifications</Typography>
                <Chip label={waEnabled ? 'Enabled' : 'Disabled'} size="small"
                  color={waEnabled ? 'success' : 'default'} />
              </Box>
              {waExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={waExpanded}>
              <Divider />
              <Box px={3} py={2.5}>
                <FormControlLabel
                  control={
                    <Switch checked={waEnabled} onChange={e => handleConfigChange('whatsapp_enabled', String(e.target.checked))} />
                  }
                  label="Enable WhatsApp Notifications"
                  sx={{ mb: 2 }}
                />
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Provider</FormLabel>
                  <RadioGroup row value={waProvider}
                    onChange={e => handleConfigChange('whatsapp_provider', e.target.value)}>
                    <FormControlLabel value="ultramsg" control={<Radio size="small" />} label="UltraMsg" />
                    <FormControlLabel value="twilio" control={<Radio size="small" />} label="Twilio" />
                    <FormControlLabel value="meta" control={<Radio size="small" />} label="Meta Cloud API" />
                  </RadioGroup>
                </FormControl>

                <Grid container spacing={2}>
                  {/* Global Recipients */}
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Global Recipients (Phone Numbers)"
                      value={config.whatsapp_recipients || ''}
                      onChange={e => handleConfigChange('whatsapp_recipients', e.target.value)}
                      placeholder="+919876543210, +919812345678"
                      helperText="Comma-separated phone numbers in E.164 format" multiline rows={2} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Global WhatsApp Groups"
                      value={config.whatsapp_groups || ''}
                      onChange={e => handleConfigChange('whatsapp_groups', e.target.value)}
                      placeholder="120363xxxxxxxxxx@g.us"
                      helperText="Comma-separated group IDs (UltraMsg only)" multiline rows={2} />
                  </Grid>

                  {/* Content reminder schedule (daily "not approved" digest) */}
                  <Grid item xs={12}>
                    <Divider textAlign="left" sx={{ my: 0.5 }}>
                      <Chip label="Content Reminder Schedule" size="small" />
                    </Divider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="time" label="Daily Reminder Time (IST)"
                      value={config.content_reminder_time || '10:30'}
                      onChange={e => handleConfigChange('content_reminder_time', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="When the 'content not approved' reminder is sent each day" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="number" label="Overdue Threshold (days)"
                      value={config.content_overdue_days || '3'}
                      onChange={e => handleConfigChange('content_overdue_days', e.target.value)}
                      inputProps={{ min: 1 }}
                      helperText="Content not approved beyond this many days is flagged overdue" />
                  </Grid>

                  {/* UltraMsg */}
                  {waProvider === 'ultramsg' && <>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Instance ID" value={config.whatsapp_instance_id || ''}
                        onChange={e => handleConfigChange('whatsapp_instance_id', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Token" type="password" value={config.whatsapp_token || ''}
                        onChange={e => handleConfigChange('whatsapp_token', e.target.value)} />
                    </Grid>
                  </>}

                  {/* Twilio */}
                  {waProvider === 'twilio' && <>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Account SID" value={config.whatsapp_twilio_sid || ''}
                        onChange={e => handleConfigChange('whatsapp_twilio_sid', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Auth Token" type="password" value={config.whatsapp_twilio_token || ''}
                        onChange={e => handleConfigChange('whatsapp_twilio_token', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="From Number" value={config.whatsapp_twilio_from || ''}
                        onChange={e => handleConfigChange('whatsapp_twilio_from', e.target.value)}
                        placeholder="+14155238886" />
                    </Grid>
                  </>}

                  {/* Meta Cloud */}
                  {waProvider === 'meta' && <>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Access Token" type="password" value={config.whatsapp_meta_access_token || ''}
                        onChange={e => handleConfigChange('whatsapp_meta_access_token', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Phone Number ID" value={config.whatsapp_meta_phone_number_id || ''}
                        onChange={e => handleConfigChange('whatsapp_meta_phone_number_id', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="API Version" value={config.whatsapp_meta_api_version || ''}
                        onChange={e => handleConfigChange('whatsapp_meta_api_version', e.target.value)}
                        placeholder="v21.0" />
                    </Grid>
                  </>}
                </Grid>
                <Box mt={2} display="flex" gap={1}>
                  <Button size="small" variant="outlined" startIcon={<SendIcon />}
                    onClick={() => handleTest('whatsapp')} disabled={testing}>
                    Send Test WhatsApp
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<SendIcon />}
                    onClick={() => handleTest('both')} disabled={testing}>
                    Test Both
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {/* Save Settings Button */}
          <Box mb={3} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={handleSaveSettings} disabled={saving}>
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Box>

          {/* Event Configuration */}
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box px={2.5} py={2} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1.5}>
                <EventIcon color="action" />
                <Typography fontWeight={600}>Event Configuration</Typography>
                <Chip label={`${events.length} events`} size="small" variant="outlined" />
              </Box>
              <Button size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={() => setAddEventDialog(true)}>
                Add Event
              </Button>
            </Box>
            <Divider />
            <Box px={2.5} py={1.5}>
              <Typography variant="caption" color="text.secondary">
                Toggle each event on/off, and optionally override which WhatsApp recipients or groups receive it.
                Leave the per-event fields blank to use the global Recipients / Groups configured above.
              </Typography>
            </Box>

            {Object.entries(groupedEvents).map(([category, catEvents]) => (
              <Box key={category} mb={1}>
                <Box px={2.5} py={1}>
                  <Chip label={category} size="small"
                    sx={{ bgcolor: CATEGORY_COLORS[category] || '#546e7a', color: '#fff', fontWeight: 600 }} />
                </Box>
                {catEvents.map(ev => {
                  const isEnabled = config[`event_${ev.type}`] === 'true';
                  const isExpanded = expandedEvent === ev.type;
                  return (
                    <Box key={ev.type} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box px={2.5} py={1.5} display="flex" alignItems="center" gap={1}>
                        <IconButton size="small" onClick={() => setExpandedEvent(isExpanded ? null : ev.type)}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                        <Typography variant="body2" sx={{ flex: 1 }}>{ev.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontFamily: 'monospace' }}>
                          {ev.type}
                        </Typography>
                        <Switch size="small" checked={isEnabled}
                          onChange={e => handleEventToggle(ev.type, e.target.checked)} />
                        <Tooltip title="Send a test WhatsApp for this event">
                          <span>
                            <IconButton size="small" color="success" disabled={testingEvent === ev.type}
                              onClick={() => handleTestEvent(ev.type)}>
                              {testingEvent === ev.type
                                ? <CircularProgress size={16} />
                                : <WhatsAppIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete event">
                          <IconButton size="small" color="error" onClick={() => handleDeleteEvent(ev.type)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Collapse in={isExpanded}>
                        <Box px={5} pb={2} pt={0.5} sx={{ bgcolor: 'action.hover' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="Recipients (override)"
                                value={config[`event_${ev.type}_whatsapp_recipients`] || ''}
                                onChange={e => handleEventFieldChange(ev.type, 'whatsapp_recipients', e.target.value)}
                                placeholder="+919876543210, +919812345678"
                                helperText="Comma-separated phone numbers (E.164). Leave blank to use the global Recipients list."
                                multiline rows={2} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="WhatsApp Groups (override)"
                                value={config[`event_${ev.type}_whatsapp_groups`] || ''}
                                onChange={e => handleEventFieldChange(ev.type, 'whatsapp_groups', e.target.value)}
                                placeholder="120363xxxxxxxxxx@g.us"
                                helperText="Comma-separated group IDs (UltraMsg only). Leave blank to use the global Groups list."
                                multiline rows={2} />
                            </Grid>
                            <Grid item xs={12}>
                              <Button size="small" variant="outlined"
                                onClick={() => handleSaveEventOverrides(ev.type)}>
                                Save Overrides
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            ))}

            {events.length === 0 && (
              <Box px={3} py={4} textAlign="center">
                <Typography color="text.secondary">No events configured. Click "Add Event" to get started.</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* ── Delivery Logs Tab ── */}
      {tab === 1 && (
        <Box>
          {/* Filters */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField size="small" label="Event Type" value={logFilters.event_type}
              onChange={e => setLogFilters(p => ({ ...p, event_type: e.target.value }))}
              placeholder="e.g. leave_applied" sx={{ minWidth: 200 }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Channel</InputLabel>
              <Select value={logFilters.channel} label="Channel"
                onChange={e => setLogFilters(p => ({ ...p, channel: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={logFilters.status} label="Status"
                onChange={e => setLogFilters(p => ({ ...p, status: e.target.value }))}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" onClick={() => { setLogPage(1); loadLogs(); }}>
              Search
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>#</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No delivery logs found
                    </TableCell>
                  </TableRow>
                ) : logs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell>{log.id}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.eventType}</Typography>
                    </TableCell>
                    <TableCell><ChannelChip channel={log.channel} /></TableCell>
                    <TableCell><StatusChip status={log.status} /></TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={log.subject || ''}><span>{log.subject || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, color: 'error.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={log.errorMessage || ''}><span>{log.errorMessage || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell>
                      {log.referenceId ? (
                        <Typography variant="caption">{log.referenceType}: {log.referenceId}</Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption">
                        {log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {logTotal > 50 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination count={Math.ceil(logTotal / 50)} page={logPage}
                onChange={(_, p) => setLogPage(p)} color="primary" />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {logTotal} total log entries
          </Typography>
        </Box>
      )}

      {/* Add Event Dialog */}
      <Dialog open={addEventDialog} onClose={() => setAddEventDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Custom Event</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth size="small" label="Event Type (slug)" value={newEvent.type}
              onChange={e => setNewEvent(p => ({ ...p, type: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
              placeholder="e.g. content_scheduled" helperText="Lowercase letters, numbers, underscores only" />
            <TextField fullWidth size="small" label="Display Label" value={newEvent.label}
              onChange={e => setNewEvent(p => ({ ...p, label: e.target.value }))}
              placeholder="e.g. Content Scheduled for Release" />
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={newEvent.category} label="Category"
                onChange={e => setNewEvent(p => ({ ...p, category: e.target.value }))}>
                {['Content', 'Employee', 'Leave', 'Expense', 'Tasks', 'Recruitment', 'Salary', 'Inventory', 'Custom'].map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEventDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddEvent}>Add Event</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
